import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Tooltip,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { useThemeName } from '../../hooks/useThemeName';
import { reverseGeocode } from '../../utils/geocoding';
import { waypointRole, waypointLabel, formatDistance, formatDuration } from '../../utils/formatters';
import { chaikinSmooth, findInsertionIndex, nearestPointOnPolylines } from '../../utils/geomath';
import { snapToRoad, fetchRoadGeometry } from '../../utils/snapRoad';
import { registerMap } from '../../utils/mapController';
import { gradeColor, GRADE_LEGEND } from '../../utils/elevation';
import { fetchDiscoveryImages } from '../../services/poiService';
import './MapView.css';

delete L.Icon.Default.prototype._getIconUrl;

function createWaypointIcon(label, role) {
  const colors = { start: '#00c2a8', end: '#f5564a', mid: '#ff7a1a' };
  const bg = colors[role] || colors.mid;
  return L.divIcon({
    className: '',
    html: `<div class="waypoint-marker waypoint-marker--${role}"><span>${label}</span></div>`,
    iconSize: [28, 34],
    iconAnchor: [14, 34],
    popupAnchor: [0, -34],
  });
}

const dragHandleIcon = L.divIcon({
  className: '',
  html: '<div class="route-drag-handle"></div>',
  iconSize: [13, 13],
  iconAnchor: [6, 6],
});

// ── POI markers (fuel / viewpoints / food) ─────────────────────────────────
// Inline SVG (not emoji) so the glyph always renders + takes the category
// colour — emoji like 🏞 render white-on-white on some platforms.
const svg = (paths) =>
  `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
const POI_SVG = {
  fuel: svg('<line x1="3" y1="22" x2="15" y2="22"/><line x1="4" y1="9" x2="14" y2="9"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 4 0V9.8a2 2 0 0 0-.6-1.4L18 5"/>'),
  viewpoint: svg('<path d="m3 20 6-11 4 6 3-4 5 9z"/><circle cx="8" cy="6" r="2"/>'),
  food: svg('<path d="M17 8h1a4 4 0 0 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>'),
};
const POI_LABEL = { fuel: 'Fuel station', viewpoint: 'Viewpoint', food: 'Food stop' };
function createPoiIcon(cat, active = false) {
  return L.divIcon({
    className: '',
    html: `<div class="poi-marker poi-marker--${cat}${active ? ' poi-marker--active' : ''}">${POI_SVG[cat]}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

// Category-coloured cluster bubble (count) at low zoom; expands to pins on zoom.
function clusterIcon(cat) {
  return (cluster) => L.divIcon({
    className: '',
    html: `<div class="poi-cluster poi-cluster--${cat}">${cluster.getChildCount()}</div>`,
    iconSize: [30, 30],
  });
}

function POIMarkers() {
  const { state } = useRoute();
  const { pois, poiPins, selectedMapItem } = state;
  if (!pois) return null;
  return ['fuel', 'viewpoint', 'food']
    .filter(cat => poiPins[cat] && pois[cat]?.length)
    .map(cat => (
      <MarkerClusterGroup
        key={cat}
        chunkedLoading
        maxClusterRadius={45}
        showCoverageOnHover={false}
        iconCreateFunction={clusterIcon(cat)}
      >
        {pois[cat].map((p, i) => {
          const active = selectedMapItem?.kind === 'poi' && selectedMapItem.cat === cat && selectedMapItem.i === i;
          return (
            <Marker
              key={i}
              position={[p.lat, p.lng]}
              icon={createPoiIcon(cat, active)}
              zIndexOffset={active ? 1000 : 0}
            >
              {/* key forces a remount on select so Leaflet re-binds with the
                  new `permanent` option — it isn't reactive to prop changes
                  on an already-bound tooltip. Sticks open (no hover needed)
                  once a POI's list row is clicked. */}
              <Tooltip key={active ? 'active' : 'inactive'} permanent={active} direction="top" offset={[0, -10]}>
                {p.name || POI_LABEL[cat]}
              </Tooltip>
            </Marker>
          );
        })}
      </MarkerClusterGroup>
    ));
}

// Wikipedia "worth a stop" places → camera markers with a photo tile on hover.
const DISC_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';
function discIcon(active = false) {
  return L.divIcon({
    className: '',
    html: `<div class="poi-marker poi-marker--disc${active ? ' poi-marker--active' : ''}">${DISC_SVG}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    tooltipAnchor: [0, -12],
  });
}
const DISC_ICON = discIcon(false);
const DISC_ICON_ACTIVE = discIcon(true);

// Horizontally-swipeable photo strip inside a discovery's tooltip card. Needs
// the Tooltip's `interactive` option (set below) — Leaflet tooltips ignore
// pointer events by default, which would make the scroll/nav-button clicks
// pass straight through to the map underneath.
function DiscoveryGallery({ images }) {
  const trackRef = useRef(null);
  const scrollBy = dir => trackRef.current?.scrollBy({ left: dir * trackRef.current.clientWidth, behavior: 'smooth' });
  return (
    <div className="disc-tip__gallery">
      <div className="disc-tip__gallery-track" ref={trackRef}>
        {images.map((src, i) => (
          <img key={src} className="disc-tip__img" src={src} alt="" loading={i === 0 ? 'eager' : 'lazy'} />
        ))}
      </div>
      {images.length > 1 && (
        <>
          <button type="button" className="disc-tip__nav disc-tip__nav--prev" onClick={() => scrollBy(-1)} aria-label="Previous photo">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button type="button" className="disc-tip__nav disc-tip__nav--next" onClick={() => scrollBy(1)} aria-label="Next photo">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div className="disc-tip__dots">
            {images.map(src => <span key={src} className="disc-tip__dot" />)}
          </div>
        </>
      )}
    </div>
  );
}

function DiscoveryMarkers() {
  const { state } = useRoute();
  const { discoveries, poiPins, selectedMapItem } = state;
  // Extra photos per place, fetched lazily on hover (see loadImages) — most
  // of the ~30 discoveries on a route are never inspected, so this keeps the
  // upfront discoveries fetch to its one cheap thumbnail per place.
  const [extraImages, setExtraImages] = useState({}); // placeId -> string[]
  const fetchedRef = useRef(new Set());
  // Which place's tooltip is pinned open by hover — plain Leaflet hover-open
  // closes the instant the cursor leaves the marker icon, which is before it
  // ever reaches the tooltip floating above it, so a rider could never
  // actually move the mouse up into the gallery to swipe through it. This
  // keeps it open across that gap (and while over the card itself), closing
  // only after a short grace period with the cursor over neither.
  const [hoveredId, setHoveredId] = useState(null);
  const closeTimerRef = useRef(null);
  const cancelClose = useCallback(() => clearTimeout(closeTimerRef.current), []);
  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setHoveredId(null), 200);
  }, []);

  const loadImages = useCallback(place => {
    if (fetchedRef.current.has(place.id)) return;
    fetchedRef.current.add(place.id);
    fetchDiscoveryImages(place.title)
      .then(images => { if (images.length) setExtraImages(prev => ({ ...prev, [place.id]: images })); })
      .catch(() => {});
  }, []);

  // Selecting a discovery from the sidebar list pins its tooltip open without
  // a hover event ever firing — load its extra photos too, not just on hover.
  useEffect(() => {
    if (selectedMapItem?.kind !== 'discovery') return;
    const place = discoveries?.find(p => p.id === selectedMapItem.id);
    if (place) loadImages(place);
  }, [selectedMapItem, discoveries, loadImages]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  if (!discoveries?.length || !poiPins.discoveries) return null;
  return discoveries.map(place => {
    const active = selectedMapItem?.kind === 'discovery' && selectedMapItem.id === place.id;
    const open = active || hoveredId === place.id;
    const gallery = [place.thumb, ...(extraImages[place.id] || [])].filter((src, i, arr) => src && arr.indexOf(src) === i);
    return (
      <Marker
        key={place.id}
        position={[place.lat, place.lng]}
        icon={active ? DISC_ICON_ACTIVE : DISC_ICON}
        zIndexOffset={active ? 1000 : 0}
        eventHandlers={{
          mouseover: () => { cancelClose(); setHoveredId(place.id); loadImages(place); },
          mouseout: scheduleClose,
        }}
      >
        {/* key forces a remount whenever open/closed flips so Leaflet re-binds
            with the new `permanent` option — it isn't reactive to prop changes
            on an already-bound tooltip (same reason the `active` case already
            needed this before hover-pinning existed). */}
        <Tooltip
          key={open ? 'open' : 'closed'}
          permanent={open}
          direction="top"
          offset={[0, -12]}
          className="disc-tip"
          opacity={1}
          interactive
        >
          <div
            className="disc-tip__card"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            // `interactive` on the Tooltip only makes this DOM node receive
            // pointer events at all (Leaflet tooltips are pointer-events:none
            // by default) — it does NOT stop a click from also bubbling up to
            // the map's own click-to-add-waypoint handler underneath, since
            // that's a plain DOM event bubbling the whole way to the map
            // container. Leaflet's own utility for exactly this (embedding
            // interactive HTML in a tooltip/popup) stops it at the source.
            ref={el => { if (el) { L.DomEvent.disableClickPropagation(el); L.DomEvent.disableScrollPropagation(el); } }}
          >
            {gallery.length > 0 && <DiscoveryGallery images={gallery} />}
            <div className="disc-tip__body">
              <div className="disc-tip__name">{place.title}</div>
              {place.extract && <p className="disc-tip__desc">{place.extract}</p>}
            </div>
          </div>
        </Tooltip>
      </Marker>
    );
  });
}

// ── Snap-to-road hover preview + click-to-add ─────────────────────────────
// Road geometry for the visible area is fetched ONCE when the map settles
// (zoom >= 14), then every mousemove snaps locally in <1 ms — no network.
// The indicator lerps toward the snapped point each frame for a fluid glide.
const SNAP_MIN_ZOOM = 13;
const SNAP_RADIUS_PX = 34; // cursor must be within ~34px of a road

function SnapAddHandler({ mode }) {
  const { state, dispatch } = useRoute();
  const map = useMap();
  const roadsRef    = useRef({ bounds: null, lines: [] });
  const cooldownRef = useRef(0);
  const targetRef   = useRef(null);  // where the dot should be (null = hidden)
  const curRef      = useRef(null);  // where the dot currently is (animated)
  const rafRef      = useRef(null);
  const haloRef     = useRef(null);
  const dotRef      = useRef(null);
  const rubberRef   = useRef(null);  // dashed line from last waypoint to cursor
  const visibleRef  = useRef(false);
  const fetchTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);

  // Latest mode + last waypoint, read inside the persistent map handlers.
  const modeRef = useRef(mode); modeRef.current = mode;
  const lastPointRef = useRef(null);
  const waypointsRef = useRef([]); waypointsRef.current = state.current.waypoints;
  {
    const wps = state.current.waypoints.filter(w => w.lat != null && w.lng != null);
    lastPointRef.current = wps.length ? wps[wps.length - 1] : null;
  }

  // Pan mode → grab cursor; edit mode lets the snap preview drive the cursor.
  useEffect(() => {
    if (mode === 'pan') map.getContainer().style.cursor = 'grab';
    else map.getContainer().style.cursor = '';
    return () => { map.getContainer().style.cursor = ''; };
  }, [mode, map]);

  // Imperative layers — avoids a React re-render per mousemove frame.
  // A dedicated pane keeps the indicator above route lines and markers.
  useEffect(() => {
    if (!map.getPane('snap-preview')) {
      map.createPane('snap-preview').style.zIndex = 650;
    }
    haloRef.current = L.circleMarker([0, 0], {
      pane: 'snap-preview',
      radius: 13, color: '#2563eb', weight: 1.5, opacity: 0,
      fillColor: '#2563eb', fillOpacity: 0, interactive: false,
    }).addTo(map);
    dotRef.current = L.circleMarker([0, 0], {
      pane: 'snap-preview',
      radius: 5, color: '#ffffff', weight: 2, opacity: 0,
      fillColor: '#2563eb', fillOpacity: 0, interactive: false,
    }).addTo(map);
    // Kurviger-style dashed "rubber band" from the last waypoint to the cursor.
    rubberRef.current = L.polyline([], {
      pane: 'snap-preview',
      color: '#2563eb', weight: 3, opacity: 0,
      dashArray: '1 10', lineCap: 'round', interactive: false,
    }).addTo(map);
    return () => {
      haloRef.current?.remove();
      dotRef.current?.remove();
      rubberRef.current?.remove();
      cancelAnimationFrame(rafRef.current);
      clearTimeout(fetchTimerRef.current);
      fetchAbortRef.current?.abort();
      map.getContainer().style.cursor = '';
    };
  }, [map]);

  function setVisible(on) {
    if (visibleRef.current === on) return;
    visibleRef.current = on;
    haloRef.current?.setStyle({ opacity: on ? 0.4 : 0, fillOpacity: on ? 0.15 : 0 });
    dotRef.current?.setStyle({ opacity: on ? 1 : 0, fillOpacity: on ? 1 : 0 });
    // Pointer cursor signals "click here to drop a waypoint on this road"
    map.getContainer().style.cursor = on ? 'pointer' : '';
  }

  function animate() {
    const t = targetRef.current;
    if (!t) { rafRef.current = null; return; }
    if (!curRef.current) {
      curRef.current = { ...t }; // first show: appear directly at the road
    } else {
      curRef.current.lat += (t.lat - curRef.current.lat) * 0.35;
      curRef.current.lng += (t.lng - curRef.current.lng) * 0.35;
    }
    const ll = [curRef.current.lat, curRef.current.lng];
    haloRef.current?.setLatLng(ll);
    dotRef.current?.setLatLng(ll);
    rafRef.current = requestAnimationFrame(animate);
  }

  function hidePreview() {
    targetRef.current = null;
    curRef.current = null;
    setVisible(false);
  }

  // Hide both the snap dot AND the rubber band (leaving the map / starting a pan).
  function hideAll() {
    hidePreview();
    rubberRef.current?.setStyle({ opacity: 0 });
  }

  function loadRoads() {
    if (map.getZoom() < SNAP_MIN_ZOOM) {
      hidePreview();
      return;
    }
    const view = map.getBounds();
    // Already covered by the last fetch (we fetch a padded area, so small
    // pans inside it cost nothing) — or backing off after a failure.
    if (roadsRef.current.bounds?.contains(view)) return;
    if (Date.now() < cooldownRef.current) return;

    const b = view.pad(0.6);
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(async () => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      try {
        const lines = await fetchRoadGeometry(
          b.getSouth(), b.getWest(), b.getNorth(), b.getEast(),
          fetchAbortRef.current.signal
        );
        if (lines.length) {
          roadsRef.current = { bounds: b, lines };
        } else {
          cooldownRef.current = Date.now() + 12_000; // brief back-off on failure
        }
      } catch { /* aborted — a newer request superseded this one */ }
    }, 600);
  }

  useEffect(() => { loadRoads(); }, []);

  useMapEvents({
    moveend: loadRoads,
    zoomend: loadRoads,
    movestart: hideAll,
    mouseout: hideAll,
    mousemove(e) {
      // Rubber band: dashed line from the last waypoint to the cursor (edit mode).
      const last = lastPointRef.current;
      if (modeRef.current === 'edit' && last && !map._roamrOverRoute) {
        rubberRef.current?.setLatLngs([[last.lat, last.lng], [e.latlng.lat, e.latlng.lng]]);
        rubberRef.current?.setStyle({ opacity: 0.8 });
      } else {
        rubberRef.current?.setStyle({ opacity: 0 });
      }

      // Pan mode: no snap preview, no click-to-add.
      if (modeRef.current === 'pan') { hidePreview(); return; }
      // While hovering the route itself, defer to the elevation/insert handle.
      if (map._roamrOverRoute) { hidePreview(); return; }
      const { lines } = roadsRef.current;
      if (!lines.length || map.getZoom() < SNAP_MIN_ZOOM) { hidePreview(); return; }
      // Pixel-consistent threshold: ~30px at the current zoom
      const mpp = 40075016.7 * Math.cos((e.latlng.lat * Math.PI) / 180) / (256 * 2 ** map.getZoom());
      const snap = nearestPointOnPolylines(e.latlng.lat, e.latlng.lng, lines, SNAP_RADIUS_PX * mpp);
      if (snap) {
        targetRef.current = snap;
        setVisible(true);
        if (!rafRef.current) rafRef.current = requestAnimationFrame(animate);
      } else {
        hidePreview();
      }
    },
    click: async (e) => {
      if (modeRef.current === 'pan') return; // pan mode: clicks don't add waypoints
      const { lat, lng } = e.latlng;
      // Prefer the live local snap; then local snap with a wider net;
      // then OSRM as a network fallback (e.g. low zoom levels).
      let pt = targetRef.current;
      if (!pt && roadsRef.current.lines.length) {
        pt = nearestPointOnPolylines(lat, lng, roadsRef.current.lines, 500);
      }
      const initial = pt ?? { lat, lng };

      // Mirrors ADD_WAYPOINT's own insertion logic (before any loop-via/end
      // markers) so the background refinement below can target the exact
      // waypoint it just placed, by index, once snap/name resolve.
      const wps = waypointsRef.current;
      let insertAt = wps.length;
      for (let i = wps.length - 1; i >= 0; i--) {
        if (!wps[i].isLoopVia && !wps[i].isLoopEnd) { insertAt = i + 1; break; }
      }

      // Drop the pin immediately — snapping to the road and looking up its
      // name happen in the background, so placing a point never waits on a
      // network round-trip to even appear.
      dispatch({ type: 'ADD_WAYPOINT', payload: { lat: initial.lat, lng: initial.lng, name: '', pending: true } });
      hidePreview();

      let roadName = '';
      if (!pt) {
        const snapped = await snapToRoad(lat, lng, { maxDistM: 500 }).catch(() => null);
        if (snapped) { pt = snapped; roadName = snapped.roadName || ''; }
      }
      const f = pt ?? initial;
      if (f.lat !== initial.lat || f.lng !== initial.lng) {
        dispatch({ type: 'UPDATE_WAYPOINT', payload: { index: insertAt, lat: f.lat, lng: f.lng } });
      }
      const name = await reverseGeocode(f.lat, f.lng);
      dispatch({ type: 'UPDATE_WAYPOINT_NAME', payload: { index: insertAt, name: name || roadName || 'Dropped pin' } });
    },
  });

  return null;
}

// ── Loop variant alternatives ─────────────────────────────────────────────
function LoopVariants() {
  const { state, dispatch } = useRoute();
  const lv = state.loopVariants;
  if (!lv?.variants?.length) return null;
  const applyType = lv.kind === 'dest' ? 'APPLY_DEST_VARIANT'
    : lv.kind === 'return' ? 'APPLY_RETURN_VARIANT'
    : 'APPLY_LOOP_VARIANT';

  return lv.variants
    .filter(v => v.id !== lv.selectedId && v.geometry?.coordinates)
    .map(v => (
      <Polyline
        key={v.id}
        positions={v.geometry.coordinates.map(([lon, lat]) => [lat, lon])}
        pathOptions={{ color: v.color, weight: 4, opacity: 0.22, dashArray: '7 9', lineJoin: 'round', lineCap: 'round' }}
        eventHandlers={{
          click: (e) => {
            L.DomEvent.stopPropagation(e.originalEvent);
            dispatch({ type: applyType, payload: v });
          },
          // Dimmed at rest so the selected route stands out clearly, but
          // brightened on hover so an alternative is still easy to find and
          // aim a click at.
          mouseover: (e) => e.target.setStyle({ opacity: 0.75 }),
          mouseout:  (e) => e.target.setStyle({ opacity: 0.22 }),
        }}
      >
        <Tooltip sticky>
          {formatDistance(v.distance)} · {formatDuration(v.duration)} — tap to use
        </Tooltip>
      </Polyline>
    ));
}

// ── Expose map instance to outside components (sidebar badges etc.) ──────
function MapRegistrar() {
  const map = useMap();
  useEffect(() => {
    registerMap(map);
    return () => registerMap(null);
  }, [map]);
  return null;
}

// ── Auto-fit bounds ───────────────────────────────────────────────────────
function FitBounds({ waypoints, geometry, routeId }) {
  const map = useMap();
  const prevRef = useRef(null);

  useEffect(() => {
    const valid = waypoints.filter(wp => wp.lat != null && wp.lng != null);
    // Re-fit only when a new route is loaded (routeId changes) or the route goes
    // from empty → non-empty (first waypoint placed). Adding stops B, C, D keeps
    // the key as "filled" so the user's current zoom is never disturbed.
    const key = `${routeId}_${valid.length > 0 ? 'filled' : 'empty'}`;
    if (key === prevRef.current) return;
    prevRef.current = key;
    if (valid.length === 0) return;
    if (valid.length === 1) { map.setView([valid[0].lat, valid[0].lng], 13, { animate: true }); return; }
    const points = geometry?.coordinates
      ? geometry.coordinates.map(([lon, lat]) => [lat, lon])
      : valid.map(wp => [wp.lat, wp.lng]);
    if (points.length >= 2) map.fitBounds(points, { padding: [48, 48], animate: true, maxZoom: 15 });
  }, [waypoints, geometry, routeId]);

  return null;
}

// ── Draggable waypoint markers ────────────────────────────────────────────
function DraggableMarkers({ waypoints }) {
  const { dispatch } = useRoute();

  const entries = waypoints
    .map((wp, originalIndex) => ({ wp, originalIndex }))
    .filter(({ wp }) => wp.lat != null && wp.lng != null);

  return entries.map(({ wp, originalIndex }, visibleIndex) => {
    const role  = waypointRole(visibleIndex, entries.length);
    const label = waypointLabel(visibleIndex, entries.length);
    return (
      <Marker
        key={`wp-${originalIndex}`}
        position={[wp.lat, wp.lng]}
        icon={createWaypointIcon(label, role)}
        draggable
        zIndexOffset={visibleIndex === 0 || visibleIndex === entries.length - 1 ? 1000 : 0}
        eventHandlers={{
          dragend: async (e) => {
            const { lat, lng } = e.target.getLatLng();
            const snapped = await snapToRoad(lat, lng, { maxDistM: 500 }).catch(() => null);
            const pt = snapped ?? { lat, lng };
            // Optimistic update — keeps old name until geocode resolves
            dispatch({ type: 'UPDATE_WAYPOINT', payload: { index: originalIndex, lat: pt.lat, lng: pt.lng } });
            const name = await reverseGeocode(pt.lat, pt.lng);
            if (name) dispatch({ type: 'UPDATE_WAYPOINT_NAME', payload: { index: originalIndex, name } });
          },
        }}
      />
    );
  });
}

// ── Route polyline with hover-to-insert handle ────────────────────────────
function RouteLine({ routePositions, rawPositions, waypoints, onSvHover, elevSegments }) {
  const { dispatch } = useRoute();
  const map = useMap();
  const [handlePos, setHandlePos] = useState(null);
  const isDraggingRef  = useRef(false);
  const insertIdxRef   = useRef(null);
  const clearTimerRef  = useRef(null);
  const svTimerRef     = useRef(null);
  const onSvHoverRef   = useRef(onSvHover);    onSvHoverRef.current = onSvHover;

  const cancelHide = useCallback(() => clearTimeout(clearTimerRef.current), []);
  const scheduleHide = useCallback(() => {
    clearTimerRef.current = setTimeout(() => {
      if (!isDraggingRef.current) setHandlePos(null);
    }, 80);
  }, []);

  const handleMove = useCallback((e) => {
    cancelHide();
    map._roamrOverRoute = true; // suppress the road snap-preview while on the route
    if (!isDraggingRef.current) setHandlePos(e.latlng);
    const { lat, lng } = e.latlng;

    // Street View peek (debounced).
    clearTimeout(svTimerRef.current);
    const { clientX: x, clientY: y } = e.originalEvent;
    svTimerRef.current = setTimeout(() => {
      const heading = bearingAtPoint(lat, lng, routePositions);
      onSvHoverRef.current?.({ lat, lng, heading, x, y });
    }, 450);
  }, [routePositions, cancelHide, map]);

  const handleOut = useCallback(() => {
    scheduleHide();
    map._roamrOverRoute = false;
    clearTimeout(svTimerRef.current);
    onSvHoverRef.current?.(null);
  }, [scheduleHide, map]);

  const hasGrade = elevSegments && elevSegments.length > 0;

  // Visual grade segments — purely decorative, never intercept the mouse so the
  // single hit-line below handles all hover continuously (no flicker).
  const gradeLines = useMemo(() => (
    (elevSegments || []).map((seg, i) => (
      <Polyline
        key={i}
        positions={seg.positions}
        interactive={false}
        pathOptions={{ color: gradeColor(seg.grade), weight: 5, opacity: 0.95, lineJoin: 'round', lineCap: 'round' }}
      />
    ))
  ), [elevSegments]);

  // One continuous interactive line: visible orange when no grade, otherwise an
  // invisible fat hit-area drawn from the SAME raw coords as the coloured
  // segments so the two stay aligned at every zoom level.
  const hitPositions = hasGrade ? (rawPositions || routePositions) : routePositions;
  const hitLine = useMemo(() => (
    hitPositions && hitPositions.length >= 2 ? (
      <Polyline
        positions={hitPositions}
        pathOptions={hasGrade
          ? { color: '#000', weight: 18, opacity: 0, lineJoin: 'round', lineCap: 'round' }
          : { color: '#ff7a1a', weight: 4, opacity: 0.9, lineJoin: 'round', lineCap: 'round' }}
        eventHandlers={{ mousemove: handleMove, mouseout: handleOut }}
      />
    ) : null
  ), [hitPositions, hasGrade, handleMove, handleOut]);

  if (!routePositions || routePositions.length < 2) return null;

  return (
    <>
      {gradeLines}
      {hitLine}

      {handlePos && (
        <Marker
          position={[handlePos.lat, handlePos.lng]}
          icon={dragHandleIcon}
          draggable
          zIndexOffset={500}
          eventHandlers={{
            mouseover: cancelHide,
            mouseout: scheduleHide,
            dragstart: (e) => {
              isDraggingRef.current = true;
              const { lat, lng } = e.target.getLatLng();
              insertIdxRef.current = findInsertionIndex(lat, lng, waypoints);
            },
            drag: (e) => {
              const { lat, lng } = e.target.getLatLng();
              setHandlePos({ lat, lng });
            },
            dragend: async (e) => {
              const { lat, lng } = e.target.getLatLng();
              const snapped = await snapToRoad(lat, lng, { maxDistM: 500 }).catch(() => null);
              const pt = snapped ?? { lat, lng };
              const idx = insertIdxRef.current ?? waypoints.length;
              dispatch({ type: 'INSERT_WAYPOINT', payload: { index: idx, lat: pt.lat, lng: pt.lng, name: '' } });
              const name = await reverseGeocode(pt.lat, pt.lng);
              if (name) {
                // find the new waypoint's index after insert (it is at idx)
                dispatch({ type: 'UPDATE_WAYPOINT_NAME', payload: { index: idx, name } });
              }
              isDraggingRef.current = false;
              setHandlePos(null);
            },
          }}
        />
      )}
    </>
  );
}

// Basemap tiles — CARTO now requires an API key. This is a client-visible
// build-time value (same pattern as SV_KEY below), restricted by domain on
// CARTO's dashboard rather than kept secret — a raw Firebase Functions secret
// can't reach the browser, since tiles are fetched directly by the map, not
// proxied through a Cloud Function.
const CARTO_KEY = import.meta.env.VITE_CARTO_API_KEY;
function cartoTileUrl(path) {
  return `https://{s}.basemaps.cartocdn.com/${path}/{z}/{x}/{y}{r}.png`
    + (CARTO_KEY ? `?key=${CARTO_KEY}` : '');
}

// Satellite/aerial imagery — Esri's free World Imagery service, no API key.
// Note the {z}/{y}/{x} order (not {z}/{x}/{y}) — that's Esri's REST tile
// convention, different from the CARTO/OSM tiles above.
const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTRIBUTION =
  '&copy; <a href="https://www.esri.com/">Esri</a>, Maxar, Earthstar Geographics';

// ── Street View hover card ────────────────────────────────────────────────
const SV_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;

function segmentBearing(lat1, lng1, lat2, lng2) {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const φ1 = lat1 * Math.PI / 180, φ2 = lat2 * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function bearingAtPoint(lat, lng, positions) {
  if (!positions || positions.length < 2) return 0;
  let minD = Infinity, result = 0;
  for (let i = 0; i < positions.length - 1; i++) {
    const [alat, alng] = positions[i];
    const [blat, blng] = positions[i + 1];
    const d = (lat - (alat + blat) / 2) ** 2 + (lng - (alng + blng) / 2) ** 2;
    if (d < minD) { minD = d; result = segmentBearing(alat, alng, blat, blng); }
  }
  return result;
}

function StreetViewCard({ pos }) {
  const [phase, setPhase] = useState('idle'); // idle | loading | ok | none
  const abortRef = useRef(null);
  const fetchedKeyRef = useRef(null);

  useEffect(() => {
    if (!pos || !SV_KEY) { setPhase('idle'); return; }
    const key = `${pos.lat.toFixed(5)},${pos.lng.toFixed(5)}`;
    if (key === fetchedKeyRef.current) return;
    fetchedKeyRef.current = key;
    setPhase('loading');
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    fetch(
      `https://maps.googleapis.com/maps/api/streetview/metadata?location=${pos.lat},${pos.lng}&key=${SV_KEY}`,
      { signal: abortRef.current.signal }
    )
      .then(r => r.json())
      .then(data => setPhase(data.status === 'OK' ? 'ok' : 'none'))
      .catch(err => { if (err.name !== 'AbortError') setPhase('none'); });
    return () => abortRef.current?.abort();
  }, [pos]);

  useEffect(() => {
    if (!pos) { fetchedKeyRef.current = null; setPhase('idle'); }
  }, [pos]);

  if (!pos || !SV_KEY || phase === 'idle' || phase === 'none') return null;

  const imgUrl = phase === 'ok'
    ? `https://maps.googleapis.com/maps/api/streetview?size=300x160&location=${pos.lat},${pos.lng}&heading=${Math.round(pos.heading)}&pitch=0&fov=90&key=${SV_KEY}`
    : null;

  const W = 300, H = 196;
  let left = pos.x + 20;
  let top  = pos.y - H / 2;
  if (left + W > window.innerWidth  - 12) left = pos.x - W - 20;
  if (top < 12) top = 12;
  if (top + H > window.innerHeight - 12) top = window.innerHeight - H - 12;

  return (
    <div className={`sv-card${phase === 'ok' ? ' sv-card--ready' : ''}`} style={{ left, top }}>
      {phase === 'loading' && (
        <div className="sv-card__skeleton">
          <div className="sv-card__spinner" />
        </div>
      )}
      {phase === 'ok' && (
        <img
          className="sv-card__img"
          src={imgUrl}
          alt="Street View"
          onError={() => setPhase('none')}
        />
      )}
      <div className="sv-card__footer">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 0 0-8-8z"/>
        </svg>
        Google Street View
      </div>
    </div>
  );
}

// ── Elevation hover tooltip (single, cursor-following) ─────────────────────
// ── Main map component ────────────────────────────────────────────────────
export function MapView() {
  const { state } = useRoute();
  const { isSubscribed } = useAuth();
  const darkMap = useThemeName() === 'auris';  // single native dark tile layer
  const { current } = state;

  // Pro routes via Geoapify → credit it; free routes via OSM-based engines only.
  const tileAttribution =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
    + (isSubscribed ? ' &middot; Routing <a href="https://www.geoapify.com/">Geoapify</a>' : '');
  const { waypoints, geometry } = current;
  const elev = state.elevation;
  const [svPos, setSvPos] = useState(null);
  const [mapMode, setMapMode] = useState('edit'); // 'edit' | 'pan'
  const [mapType, setMapType] = useState('street'); // 'street' | 'satellite'

  // Routing spinner reticle: while a route is being fetched (after placing a
  // waypoint), the static cursor is hidden and this animated ring follows the
  // pointer. CSS cursors can't animate, hence a DOM overlay.
  const routingActive = state.isRouting && mapMode === 'edit';
  const reticleRef = useRef(null);
  const lastMouse = useRef({ x: -100, y: -100 });
  useEffect(() => {
    const track = (e) => { lastMouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', track, { passive: true });
    return () => window.removeEventListener('mousemove', track);
  }, []);
  useEffect(() => {
    const el = reticleRef.current;
    if (!el || !routingActive) return;
    const place = (x, y) => { el.style.left = `${x}px`; el.style.top = `${y}px`; };
    place(lastMouse.current.x, lastMouse.current.y);
    const onMove = (e) => place(e.clientX, e.clientY);
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [routingActive]);

  const routePositions = useMemo(() => {
    if (geometry?.coordinates) {
      return chaikinSmooth(geometry.coordinates, 2).map(([lon, lat]) => [lat, lon]);
    }
    const validWps = waypoints.filter(wp => wp.lat != null && wp.lng != null);
    return validWps.length >= 2 ? validWps.map(wp => [wp.lat, wp.lng]) : null;
  }, [geometry, waypoints]);

  // Raw (unsmoothed) positions — the grade-coloured segments are drawn from
  // these, so the hover hit-line must use them too or the two diverge on zoom.
  const rawPositions = useMemo(() => (
    geometry?.coordinates ? geometry.coordinates.map(([lon, lat]) => [lat, lon]) : null
  ), [geometry]);

  return (
    <div className={`map-view map-view--${mapMode}${routingActive ? ' map-view--routing' : ''}`}>
      {routingActive && (
        <div className="routing-reticle" ref={reticleRef} aria-hidden="true">
          <svg className="routing-reticle__svg" width="40" height="40" viewBox="0 0 40 40">
            <circle className="routing-reticle__track" cx="20" cy="20" r="15" />
            <circle className="routing-reticle__arc" cx="20" cy="20" r="15" />
          </svg>
          <span className="routing-reticle__dot" />
        </div>
      )}

      <div className="map-toolbar" role="group" aria-label="Map mode">
        <button
          className={`map-toolbar__btn${mapMode === 'edit' ? ' is-active' : ''}`}
          onClick={() => setMapMode('edit')}
          title="Edit — click the map to add waypoints"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          Edit
        </button>
        <button
          className={`map-toolbar__btn${mapMode === 'pan' ? ' is-active' : ''}`}
          onClick={() => setMapMode('pan')}
          title="Pan — drag to move; clicks won’t add waypoints"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"/><path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg>
          Pan
        </button>
      </div>

      <div className="map-toolbar map-toolbar--type" role="group" aria-label="Map type">
        <button
          className={`map-toolbar__btn${mapType === 'street' ? ' is-active' : ''}`}
          onClick={() => setMapType('street')}
          title="Street map"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
          Map
        </button>
        <button
          className={`map-toolbar__btn${mapType === 'satellite' ? ' is-active' : ''}`}
          onClick={() => setMapType('satellite')}
          title="Satellite imagery"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          Satellite
        </button>
      </div>

      <MapContainer center={[20, 0]} zoom={3} className="map-view__map" zoomControl={false}>
        <TileLayer
          key={mapType === 'satellite' ? 'sat' : (darkMap ? 'dark' : 'light')}
          // Dark mode reuses the same colorful Voyager tiles as light mode
          // (not CARTO's flat dark_all) and recolors them via the CSS filter
          // below — invert+hue-rotate is the standard "poor man's dark mode"
          // trick: it turns the white background black while rotating hues
          // back close to their original relationship, so roads/parks/water
          // keep distinct colors instead of going monochrome grey.
          url={mapType === 'satellite' ? SATELLITE_URL : cartoTileUrl('rastertiles/voyager')}
          className={darkMap && mapType !== 'satellite' ? 'map-tiles--night' : undefined}
          attribution={mapType === 'satellite' ? SATELLITE_ATTRIBUTION : tileAttribution}
          maxZoom={19}
        />

        <SnapAddHandler mode={mapMode} />
        <MapRegistrar />
        <FitBounds waypoints={waypoints} geometry={geometry} routeId={current.id} />
        <LoopVariants />
        <RouteLine routePositions={routePositions} rawPositions={rawPositions} waypoints={waypoints} onSvHover={setSvPos} elevSegments={elev?.segments} />
        <DraggableMarkers waypoints={waypoints} />
        <POIMarkers />
        <DiscoveryMarkers />
      </MapContainer>

      <StreetViewCard pos={svPos} />

      {elev && (
        <div className="map-view__elev-legend">
          <span className="map-view__elev-title">
            Grade
            <span className="map-view__elev-climb">↑ {elev.ascent} m</span>
          </span>
          <div className="map-view__elev-scale">
            {GRADE_LEGEND.map(g => (
              <span key={g.label} className="map-view__elev-swatch" title={g.label} style={{ background: g.color }} />
            ))}
          </div>
          <div className="map-view__elev-ends">
            <span>Steep ↓</span><span>Steep ↑</span>
          </div>
        </div>
      )}

      <div className="map-view__hint">
        Click a road to add · Drag markers or route to edit
      </div>
    </div>
  );
}
